import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useBoolean } from '@fluentui/react-hooks';
import {
  IconButton,
  IIconProps,
  Label,
  MessageBarType,
  PrimaryButton,
  SearchBox,
  Separator,
  Spinner,
  Text
} from '@fluentui/react';
import { useTranslation } from 'react-i18next';

// models
import { IdentifierTypeViewModel } from '../../../models/Entities/Person/IdentifierType/IdentifierTypeModels';

// components
import { ConfirmationButtonComponent } from '../../../commons/component/ConfirmationButton/ConfirmationButtonComponent';
import { MessageComponent } from '../../../commons/component/MessageComponent';
import IdentifierTypeFormComponent from './IdentifierType.Form.Component';

// functions
import * as CommonFunctions from '../Persons.Functions';
import * as ComponentFunctions from './IdentifierTypes.List.Functions';

const EditIcon: IIconProps = { iconName: 'Edit' };
const AddIcon: IIconProps = { iconName: 'Add' };

const IdentifierTypesListComponent = (props: ComponentFunctions.Props) => {
  const [t] = useTranslation();

  const [isFormOpen, { setTrue: openForm, setFalse: dismissForm }] = useBoolean(false);
  const [selectedIdentifierTypeId, setSelectedIdentifierTypeId] = useState<number | undefined>(undefined);
  const [searchText, setSearchText] = useState<string>('');

  useEffect(() => {
    props.GetIdentifierTypes();
  }, []);

  const normalizedSearchText = searchText.trim().toLowerCase();

  const filtered = props.identifierTypes.filter((item: IdentifierTypeViewModel) => {
    if (normalizedSearchText === '') return true;
    return [item.name, item.description]
      .filter((value) => !!value)
      .some((value) => (value as string).toLowerCase().includes(normalizedSearchText));
  });

  const handleOpenCreate = () => {
    setSelectedIdentifierTypeId(undefined);
    openForm();
  };

  const handleEdit = (identifierTypeId: number) => {
    setSelectedIdentifierTypeId(identifierTypeId);
    openForm();
  };

  const handleDismissForm = () => {
    setSelectedIdentifierTypeId(undefined);
    dismissForm();
  };

  const getJuridictionText = (item: IdentifierTypeViewModel): string => {
    const option = CommonFunctions.JuridictionTypeOptions.find((o) => o.key === item.juridictionType);
    return option ? t(option.textKey, { defaultValue: option.defaultText }) : '-';
  };

  return (
    <>
      <div className="StaffControls">
        <div className="StaffActionButtons">
          <PrimaryButton
            text={t('persons.identifierTypes.list.add', { defaultValue: 'Agregar tipo' })}
            iconProps={AddIcon}
            onClick={handleOpenCreate}
            className="actionButton"
          />
        </div>
        <SearchBox
          placeholder={t('persons.identifierTypes.list.searchPlaceholder', { defaultValue: 'Buscar tipo' })}
          underlined={false}
          onChange={(
            event?: React.ChangeEvent<HTMLInputElement> | undefined,
            newValue?: string | undefined
          ) => setSearchText(newValue || '')}
          onClear={() => setSearchText('')}
        />
      </div>
      <Separator />

      {props.failOnLoading && (
        <MessageComponent
          message={t('persons.identifierTypes.list.error', {
            defaultValue: 'No fue posible obtener los tipos de identificación'
          })}
          subMessage={props.error?.ErrorMessage || ''}
          type={MessageBarType.error}
          itemList={props.error?.Errors || []}
        />
      )}

      {!props.isLoading && filtered.length === 0 && (
        <MessageComponent
          message={t('persons.identifierTypes.list.empty', {
            defaultValue: 'No hay tipos de identificación registrados'
          })}
          type={MessageBarType.info}
        />
      )}

      {filtered.length > 0 && (
        <table className="Table">
          <thead>
            <tr>
              <th>{t('persons.identifierTypes.list.name', { defaultValue: 'Nombre' })}</th>
              <th>{t('persons.identifierTypes.list.juridictionType', { defaultValue: 'Jurisdicción' })}</th>
              <th>{t('persons.identifierTypes.list.appliesTo', { defaultValue: 'Aplica a' })}</th>
              <th>{t('persons.identifierTypes.list.mask', { defaultValue: 'Máscara' })}</th>
              <th>{t('persons.identifierTypes.list.isActive', { defaultValue: 'Activo' })}</th>
              <th style={{ width: '140px' }}>
                {t('persons.common.actions', { defaultValue: 'Acciones' })}
              </th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((item: IdentifierTypeViewModel) => (
              <tr key={item.id} className="TableBodyRow">
                <td className="primary">
                  <Label className="upper">{item.name || '-'}</Label>
                </td>
                <td>
                  <Text>{getJuridictionText(item)}</Text>
                </td>
                <td>
                  <Text>
                    {[
                      item.isForLegalPerson
                        ? t('persons.personType.natural', { defaultValue: 'Persona física' })
                        : undefined,
                      item.isForJuridicalPerson
                        ? t('persons.personType.juridical', { defaultValue: 'Persona jurídica' })
                        : undefined
                    ]
                      .filter((value) => !!value)
                      .join(' / ') || '-'}
                  </Text>
                </td>
                <td>
                  <Text>{item.identifierMask || '-'}</Text>
                </td>
                <td>
                  <Text>
                    {item.isActive
                      ? t('persons.common.yes', { defaultValue: 'Sí' })
                      : t('persons.common.no', { defaultValue: 'No' })}
                  </Text>
                </td>
                <td style={{ width: '140px' }}>
                  <IconButton
                    iconProps={EditIcon}
                    title={t('persons.common.edit', { defaultValue: 'Editar' })}
                    ariaLabel={t('persons.common.edit', { defaultValue: 'Editar' })}
                    onClick={() => handleEdit(item.id)}
                  />
                  <div className="danger">
                    <ConfirmationButtonComponent
                      ButtonId={`IdentifierTypeDeleteButton-${item.id}`}
                      ButtonIconName={'Delete'}
                      ButtonText={''}
                      ButtonCssClass={''}
                      ConfirmationButtonText={t('persons.common.delete', { defaultValue: 'Eliminar' })}
                      ConfirmationButtonCssClass={'deleteButton'}
                      CancelButtonText={t('persons.common.cancel', { defaultValue: 'Cancelar' })}
                      CancelButtonCssClass={''}
                      Title={t('persons.identifierTypes.list.deleteTitle', {
                        defaultValue: 'Eliminar tipo de identificación'
                      })}
                      Description={t('persons.identifierTypes.list.deleteDescription', {
                        defaultValue: '¿Desea eliminar el tipo de identificación seleccionado?'
                      })}
                      ElementTextHeader={t('persons.identifierTypes.list.name', { defaultValue: 'Nombre' })}
                      ElementTextInformation={item.name || ''}
                      onConfirmation={() => props.DeleteIdentifierType(item.id)}
                      onCancel={() => undefined}
                      isOperationExecuting={props.isDeleting || false}
                      isOperationExecutedWithError={props.failOnDeleting || false}
                      OperationError={props.error}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {props.isLoading && (
        <div>
          <Spinner
            label={t('persons.identifierTypes.list.loading', {
              defaultValue: 'Cargando tipos de identificación...'
            })}
            ariaLive="assertive"
            labelPosition="top"
          />
        </div>
      )}

      <IdentifierTypeFormComponent
        isOpen={isFormOpen}
        openPanel={openForm}
        dismissPanel={handleDismissForm}
        identifierTypeId={selectedIdentifierTypeId}
      />
    </>
  );
};

export default connect(
  ComponentFunctions.mapStateToProps,
  ComponentFunctions.mapDispatchToProps
)(IdentifierTypesListComponent as any);
